import styled, { css, keyframes } from 'styled-components'

export const EXPANDED_WIDTH = '220px'
export const COLLAPSED_WIDTH = '52px'
const FAVORITE_COLOR = '#F5A623'

const ease = props => props.theme.motion.easeOut
const fast = props => props.theme.motion.fast

// Testi che in chiusura svaniscono subito (prima che il bordo li tagli)
// e in apertura rientrano dopo che la sidebar si è allargata
const fadingText = css`
  transition: opacity 140ms ease 110ms, transform 200ms ${ease} 110ms;

  ${props => props.$collapsed && css`
    opacity: 0;
    transform: translateX(-6px);
    transition: opacity 80ms ease, transform 120ms ease;
  `}
`

const noMotion = css`
  @media (prefers-reduced-motion: reduce) {
    &, & * {
      transition: none !important;
      animation: none !important;
    }
  }
`

export const SidebarContainer = styled.nav`
  position: fixed;
  top: 50px;
  left: 0;
  bottom: 0;
  width: ${props => props.$collapsed ? COLLAPSED_WIDTH : EXPANDED_WIDTH};
  background: ${props => props.theme.colors.background.primary};
  border-right: 1px solid ${props => props.theme.colors.border.medium};
  display: flex;
  flex-direction: column;
  overflow: hidden;
  transition: width ${props => props.theme.motion.sidebar} ${ease}, transform ${props => props.theme.motion.sidebar} ${ease};
  z-index: 90;

  @media (max-width: 768px) {
    top: 45px;
    width: ${EXPANDED_WIDTH};
    transform: translateX(${props => props.$mobileOpen ? '0' : '-100%'});
    box-shadow: ${props => props.$mobileOpen ? `4px 0 20px ${props.theme.colors.shadow.strong}` : 'none'};
  }

  ${noMotion}
`

export const SidebarOverlay = styled.div`
  display: none;

  @media (max-width: 768px) {
    display: block;
    position: fixed;
    top: 45px;
    left: 0;
    right: 0;
    bottom: 0;
    background: ${props => props.theme.colors.shadow.overlay};
    z-index: 89;
    opacity: ${props => props.$visible ? 1 : 0};
    pointer-events: ${props => props.$visible ? 'auto' : 'none'};
    transition: opacity ${props => props.theme.motion.sidebar} ${ease};
  }

  ${noMotion}
`

export const SidebarHeader = styled.div`
  position: relative;
  height: 44px;
  flex-shrink: 0;
  border-bottom: 1px solid ${props => props.theme.colors.border.light};
`

// Larghezza fissa: mentre la sidebar si stringe il campo non si schiaccia, viene tagliato dal bordo
export const SearchBox = styled.label`
  position: absolute;
  top: 8px;
  left: 8px;
  width: 164px;
  height: 28px;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 0 8px;
  border: 1px solid ${props => props.theme.colors.border.medium};
  border-radius: ${props => props.theme.radius.input};
  color: ${props => props.theme.colors.text.muted};
  pointer-events: ${props => props.$collapsed ? 'none' : 'auto'};
  ${fadingText}

  svg {
    flex-shrink: 0;
    font-size: 14px;
  }

  &:focus-within {
    border-color: ${props => props.theme.colors.semantic.primary};
  }
`

export const SearchInput = styled.input`
  flex: 1;
  min-width: 0;
  border: none;
  outline: none;
  background: transparent;
  color: ${props => props.theme.colors.text.primary};
  font-size: ${props => props.theme.typography.fontSize.input};
  font-family: ${props => props.theme.typography.fontFamily};
`

// Ancorato a destra: da contratta (52px) resta centrato nel rail
export const PanelToggle = styled.button`
  position: absolute;
  top: 8px;
  right: 12px;
  width: 28px;
  height: 28px;
  display: grid;
  place-items: center;
  padding: 0;
  border: none;
  border-radius: ${props => props.theme.radius.button};
  background: #0095df;
  color: #fff;
  cursor: pointer;
  transition: filter ${fast} ease;

  svg {
    font-size: 18px;
  }

  &:hover {
    filter: brightness(1.15);
  }

  &:focus-visible {
    outline: 2px solid ${props => props.theme.colors.semantic.primary};
    outline-offset: 1px;
  }
`

export const SidebarList = styled.div`
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 4px 0 8px;
`

export const EmptySearch = styled.div`
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.lg};
  font-size: ${props => props.theme.typography.fontSize.small};
  color: ${props => props.theme.colors.text.muted};
`

export const Group = styled.div`
  &:first-child > button::before {
    display: none;
  }
`

// Altezza fissa sia aperta che contratta: da contratta il testo lascia il posto a una lineetta,
// così le voci non si spostano in verticale
export const GroupHeader = styled.button`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  height: 24px;
  margin-top: 4px;
  padding: 0 12px 0 18px;
  border: none;
  background: transparent;
  font-family: ${props => props.theme.typography.fontFamily};
  font-size: ${props => props.theme.typography.fontSize.label};
  font-weight: 500;
  color: ${props => props.theme.colors.text.muted};
  white-space: nowrap;
  cursor: ${props => props.$locked ? 'default' : 'pointer'};
  transition: color ${fast} ease;

  &::before {
    content: '';
    position: absolute;
    left: 14px;
    right: 14px;
    top: 50%;
    height: 1px;
    background: ${props => props.theme.colors.border.medium};
    opacity: ${props => props.$collapsed ? 1 : 0};
    transition: opacity 160ms ease;
  }

  &:hover {
    color: ${props => props.$locked ? props.theme.colors.text.muted : props.theme.colors.text.secondary};
  }

  &:focus-visible {
    outline: 2px solid ${props => props.theme.colors.semantic.primary};
    outline-offset: -2px;
  }
`

export const GroupTitle = styled.span`
  ${fadingText}
`

export const GroupChevron = styled.span`
  display: grid;
  ${fadingText}

  svg {
    font-size: 12px;
    transform: rotate(${props => props.$open ? '0deg' : '-90deg'});
    transition: transform 200ms ${ease};
  }
`

// grid-template-rows 0fr/1fr: anima l'altezza reale del contenuto senza misurarla.
// visibility con ritardo: le voci di un gruppo chiuso escono dal Tab solo a fine animazione
export const GroupBody = styled.div`
  display: grid;
  grid-template-rows: ${props => props.$open ? '1fr' : '0fr'};
  transition: grid-template-rows 200ms ${ease};

  > div {
    min-height: 0;
    overflow: hidden;
    visibility: ${props => props.$open ? 'visible' : 'hidden'};
    transition: visibility 0s linear ${props => props.$open ? '0s' : '200ms'};
  }
`

const itemMoved = keyframes`
  from { opacity: 0; transform: translateY(-14px); background: rgba(250, 204, 21, 0.25); }
  to { opacity: 1; transform: none; background: transparent; }
`

export const ItemRow = styled.div`
  position: relative;
  ${p => p.$moved && css`animation: ${itemMoved} 0.5s ease-out;`}
  @media (prefers-reduced-motion: reduce) { animation: none; }

  &:hover .fav-star,
  & .fav-star:focus-visible {
    opacity: 1;
  }

  @media (hover: none) {
    .fav-star {
      opacity: 1;
    }
  }
`

// padding-left fisso: l'icona (18px) resta centrata nel rail da 52px e non si sposta mai
export const Item = styled.button`
  display: flex;
  position: relative;
  align-items: center;
  gap: 12px;
  width: 100%;
  height: ${props => props.theme.controlHeight};
  padding: 0 36px 0 17px;
  background: ${props => props.$active ? props.theme.colors.background.tertiary : 'transparent'};
  border: none;
  box-shadow: ${props => props.$active ? `inset 2px 0 0 ${props.theme.colors.text.primary}` : 'none'};
  color: ${props => props.$active ? props.theme.colors.text.primary : props.theme.colors.text.secondary};
  font-size: ${props => props.theme.typography.fontSize.input};
  font-weight: ${props => props.$active ? 600 : 400};
  font-family: ${props => props.theme.typography.fontFamily};
  text-align: left;
  white-space: nowrap;
  overflow: hidden;
  cursor: pointer;
  transition: background ${fast} ease, color ${fast} ease;

  svg {
    flex-shrink: 0;
    font-size: 18px;
  }

  &:hover {
    background: ${props => props.theme.colors.background.tertiary};
    color: ${props => props.theme.colors.text.primary};
  }

  &:focus-visible {
    outline: 2px solid ${props => props.theme.colors.semantic.primary};
    outline-offset: -2px;
  }
`

// Larghezza naturale (niente width: 0): la taglia il bordo, non si stringe durante l'animazione
export const ItemLabel = styled.span`
  flex-shrink: 0;
  ${fadingText}
`

export const FavStar = styled.button`
  position: absolute;
  top: 50%;
  right: 8px;
  transform: translateY(-50%);
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: none;
  border-radius: 50%;
  background: transparent;
  color: ${props => props.$isFavorite ? FAVORITE_COLOR : props.theme.colors.text.muted};
  opacity: ${props => props.$isFavorite ? 1 : 0};
  cursor: pointer;
  transition: opacity ${fast} ease;

  &:hover {
    color: ${FAVORITE_COLOR};
    background: ${props => props.theme.colors.background.hover};
  }
`

export const NewBadge = styled.span`
  margin-left: auto;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: ${props => props.theme.colors.semantic.danger};
  color: ${props => props.theme.colors.text.inverse};
  font-size: 0.65rem;
  font-weight: 700;
  flex-shrink: 0;

  ${props => props.$collapsed && `
    position: absolute;
    top: 6px;
    left: 31px;
    min-width: 8px;
    width: 8px;
    height: 8px;
    padding: 0;
    margin: 0;
  `}
`

const tipIn = keyframes`
  from { opacity: 0; transform: translate(-4px, -50%); }
  to { opacity: 1; transform: translate(0, -50%); }
`

// Fuori dalla lista scrollabile (position: fixed), altrimenti overflow lo taglierebbe.
// top passato via style={{ top }} per non generare una classe per ogni valore
export const Tooltip = styled.div`
  position: fixed;
  left: calc(${COLLAPSED_WIDTH} + 8px);
  transform: translate(0, -50%);
  padding: 4px 8px;
  border-radius: 6px;
  background: ${props => props.theme.colors.text.primary};
  color: ${props => props.theme.colors.text.inverse};
  font-family: ${props => props.theme.typography.fontFamily};
  font-size: ${props => props.theme.typography.fontSize.small};
  white-space: nowrap;
  pointer-events: none;
  z-index: 95;
  animation: ${tipIn} ${fast} ${ease};

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`

export const MobileTrigger = styled.button`
  display: none;

  @media (max-width: 768px) {
    display: ${props => props.$hidden ? 'none' : 'flex'};
    align-items: center;
    justify-content: center;
    position: fixed;
    top: 4px;
    left: 12px;
    width: 37px;
    height: 37px;
    border-radius: ${props => props.theme.radius.button};
    border: none;
    background: #0095df;
    color: #fff;
    box-shadow: 0 4px 12px ${props => props.theme.colors.shadow.medium};
    z-index: 101;
    cursor: pointer;

    svg {
      font-size: 20px;
    }
  }
`
